import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getStoredLang, setStoredLang, translate, SUPPORTED_LANGS } from "@/lib/i18n";

/**
 * App-wide language context.
 *
 * Behaviour:
 *  - On boot, reads `useen_lang` from localStorage. Falls back to "en" when
 *    nothing is stored — explicit user choice never gets overridden, and no
 *    automatic browser-language redirect happens.
 *  - `setLang(lang)` persists the choice.
 *  - `syncFromUrl(lang)` is used by the public landing routes (`/` and `/tr`)
 *    so that *visiting* a landing route also captures the user's intent
 *    when they have NOT explicitly picked yet.
 */

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => getStoredLang() || "en");

  // Keep the <html lang="..."> attribute in sync so screen readers and
  // search engines pick up the right locale across the whole app.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED_LANGS.includes(next)) return;
    setStoredLang(next);
    setLangState(next);
  }, []);

  // Called by the landing page on mount based on URL ("/" → en, "/tr" → tr).
  // Only updates storage when the user has NO explicit pref yet OR is actively
  // viewing the matching landing route. We always honour the URL the user
  // chose to visit.
  const syncFromUrl = useCallback((urlLang) => {
    if (!SUPPORTED_LANGS.includes(urlLang)) return;
    setStoredLang(urlLang);
    setLangState(urlLang);
  }, []);

  const t = useCallback((key, params) => translate(lang, key, params), [lang]);

  const value = useMemo(
    () => ({ lang, setLang, syncFromUrl, t }),
    [lang, setLang, syncFromUrl, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside <LanguageProvider>");
  return ctx;
}

// Convenience hook for components that only need the translator.
export function useT() {
  return useLang().t;
}
